<template>
  <section :class="$style.userGroupSummaryDetails">
    <UserGroupItemSummary
      :itemSummary="suggestionsSummary"
      :wasDataFetched="wasDataFetched"
    />
    <section
      v-if="hasGlobalSudo"
      :class="$style.elevatedPermissions"
    >
      <h3 :class="$style.elevatedPermissionsTitle">
        Elevated Permissions
      </h3>
      <div :class="$style.elevatedPermissionsContainer">
        <img
          :class="$style.elevatedPermissionsIcon"
          :src="globalSudoResourceMapping.iconLocation"
        >
        <span :class="$style.elevatedPermissionsText">
          Global {{ globalSudoResourceMapping.title }}
        </span>
      </div>
    </section>
    <UserGroupMultipleItemsSummary
      :itemSummary="assignedResourcesSummary"
      :wasDataFetched="wasDataFetched"
    />
    <UserGroupItemSummary
      :itemSummary="attributesSummary"
      :wasDataFetched="wasDataFetched"
    />
  </section>
</template>

<script>
import { assignedResourcesMapping, itemSummaries, sudoResourcesMapping } from 'client/components/ItemTable/BodyComponents/UserGroupSummary/UserGroupResourcesMap';
import { get, omit, startCase } from 'lodash';
import { mapActions } from 'vuex';
import { notify } from 'client/util';
import Constants, {
  ItemTypeConsts,
  StoreModuleMap,
  getDetailsRoute,
} from 'client/util/Constants';
import GroupsConstants from 'client/util/Constants/GroupsConstants';
import ItemSummary from 'client/components/ItemTable/BodyComponents/UserGroupSummary/ItemSummary';
import UserGroupItemSummary from 'client/components/ItemTable/BodyComponents/UserGroupSummary/UserGroupItemSummary.vue';
import UserGroupMultipleItemsSummary from 'client/components/ItemTable/BodyComponents/UserGroupSummary/UserGroupMultipleItemsSummary.vue';

const { reservedGroupAttributes } = GroupsConstants;
const userGroupCollection = StoreModuleMap[ItemTypeConsts.userGroup];

export default {
  name: 'UserGroupSummaryDetails',

  components: {
    UserGroupItemSummary,
    UserGroupMultipleItemsSummary,
  },

  props: {
    isExpanded: {
      default: false,
      type: Boolean,
    },
    item: {
      default: () => ({}),
      type: Object,
    },
  },

  data() {
    return {
      attributesSummary: itemSummaries.attributesSummary(),
      assignedResourcesSummary: itemSummaries.assignedResourcesSummary(),
      suggestionsSummary: itemSummaries.suggestionsSummary({
        fallbackLinkUrl: getDetailsRoute(ItemTypeConsts.userGroup, this.item),
      }),
      wasDataFetched: false,
      sudo: {},
    };
  },

  computed: {
    globalSudoResourceMapping() {
      const isPasswordless = get(this.sudo || {}, 'withoutPassword', false);
      return this.hasGlobalSudo && isPasswordless
        ? sudoResourcesMapping.sudoWithoutPassword
        : sudoResourcesMapping.sudo;
    },
    hasGlobalSudo() {
      return Object.values(this.sudo || {}).some(k => !!k);
    },
  },

  watch: {
    isExpanded(value) {
      if (value && !this.wasDataFetched) {
        this.fetchGroupSummary();
      }
    },
  },

  methods: {
    ...mapActions(userGroupCollection, ['fetchGroupSummaryResources']),

    buildAttributesString(userGroupData = {}) {
      const attributes = get(userGroupData, 'attributes', {});
      const filteredAttributes = omit(attributes, reservedGroupAttributes);
      return Object.keys(filteredAttributes).map((key) => {
        let connectWord;
        let value = filteredAttributes[key];
        if (typeof value !== 'boolean') {
          connectWord = 'must be';
        } else {
          connectWord = '=';
        }
        if (typeof value === 'object') {
          value = JSON.stringify(value);

          return `${key} ${connectWord} ${value}`;
        }
        return `${startCase(key)} ${connectWord} ${value}`;
      });
    },

    buildResourcesData(assignedResources = {}) {
      const resourcesList = [];
      const entries = Object.entries(assignedResources);
      const fallbackLinkUrl = getDetailsRoute(ItemTypeConsts.userGroup, this.item);

      entries.forEach(([resourceName, resourceList = []]) => {
        const { listMapping, ...resourceBase } = assignedResourcesMapping[
          resourceName
        ];

        const subItemsList = resourceName === 'systemGroups'
          ? this.buildSudoResourceData(resourceList) : [];

        resourcesList.push(
          new ItemSummary({
            ...resourceBase,
            fallbackLinkUrl,
            openLinkInNewTab: false,
            items: resourceList.map(listMapping),
            subItems: subItemsList,
          }),
        );
      });

      return resourcesList;
    },

    buildSudoResourceData(resourceList) {
      const newSudoResourceList = [];
      const devicesWithSudo = resourceList.filter((item) => item.compiledAttributes
      && item.compiledAttributes.sudo
      && item.compiledAttributes.sudo.enabled
      && !item.compiledAttributes.sudo.withoutPassword);
      const devicesWithPasswordlessSudo = resourceList.filter((item) => item.compiledAttributes
      && item.compiledAttributes.sudo
      && item.compiledAttributes.sudo.enabled
      && item.compiledAttributes.sudo.withoutPassword);

      if (devicesWithSudo.length) {
        const {
          title, iconLocation, fallbackLinkText, fallbackMessage,
        } = sudoResourcesMapping.sudo;
        newSudoResourceList.push(
          new ItemSummary({
            title,
            iconLocation,
            fallbackLinkText,
            fallbackMessage,
            fallbackLinkUrl: '',
            openLinkInNewTab: false,
            items: devicesWithSudo.map((item) => item.name),
          }),
        );
      }

      if (devicesWithPasswordlessSudo.length) {
        const {
          title, iconLocation, fallbackLinkText, fallbackMessage,
        } = sudoResourcesMapping.sudoWithoutPassword;
        newSudoResourceList.push(
          new ItemSummary({
            title,
            iconLocation,
            fallbackLinkText,
            fallbackMessage,
            fallbackLinkUrl: '',
            openLinkInNewTab: false,
            items: devicesWithPasswordlessSudo.map((item) => item.name),
          }),
        );
      }

      return newSudoResourceList;
    },

    buildSuggestionsString(userGroupData = {}) {
      const filters = get(userGroupData, 'memberQuery.filters', []);
      return filters.map(
        filter => `${startCase(filter.field)} must be ${filter.value}`,
      );
    },

    buildSummary({ userGroupData, ...assignedResources } = {}) {
      this.suggestionsSummary.items = this.buildSuggestionsString(userGroupData);
      this.attributesSummary.items = this.buildAttributesString(userGroupData);
      this.assignedResourcesSummary.items = this.buildResourcesData(assignedResources);
      this.sudo = get(userGroupData, 'attributes.sudo', {});
    },

    async fetchGroupSummary() {
      try {
        const summaryData = await this.fetchGroupSummaryResources({ id: this.item.id });
        this.buildSummary(summaryData);
      } catch (error) {
        notify(Constants.error, `Unable to retrieve ${get(this.item, 'name', 'group')} data. Please refresh the page and try again.`);
      } finally {
        this.wasDataFetched = true;
      }
    },
  },
};
</script>

<style module>
@value summarySize: 0.923rem;

.userGroupSummaryDetails {
  display: block;
  padding: 10px 17px 23px 74px;
}

.userGroupSummaryDetails > *:not(:last-child) {
  margin-bottom: 26px;
}

.elevatedPermissions {
  font-size: summarySize;
  font-weight: bold;
}

.elevatedPermissionsTitle {
  font-size: summarySize;
  line-height: 1.6;
  margin: 0 0 5px;
}

.elevatedPermissionsContainer {
  align-items: center;
  display: flex;
  gap: 8px;
}

.elevatedPermissionsIcon {
  height: 16px;
  width: 16px;
}
</style>
